import { useGetClientSettings } from 'src/api/customer';
import { AUTH_STORAGE_KEYS, localStorageGetItem } from 'src/utils/storage-available';

import { useActingClientOptional } from './acting-client-context';

/**
 * NAT-2497 — the acting venue's commercial settings (spec §6).
 *
 * Read from `/customer/client-settings` for the venue this tab acts for —
 * keyed by venue, so a switch changes them — and only then from the fields
 * the login stored on the user, which describe the venue at sign-in time and
 * cannot follow a switch. Outside the provider, or before the settings have
 * settled, the stored fields answer as they always did.
 */
export function useVenueSettings(): {
  multiplesThreshold: number;
  showPricesWithVat: boolean;
} {
  const acting = useActingClientOptional();
  const { data } = useGetClientSettings(acting?.actingClientId ?? null);

  // The snapshot answers only while the venue's settings have NOT settled.
  // A settled null is the venue's own answer (no threshold, no VAT display),
  // never the login venue's value standing in for another venue's.
  if (data === undefined) {
    let stored: { multiplesThreshold?: number; showPricesWithVat?: boolean } = {};
    try {
      stored = JSON.parse(localStorageGetItem(AUTH_STORAGE_KEYS.user) || '{}');
    } catch {
      stored = {};
    }
    return {
      multiplesThreshold: stored.multiplesThreshold ?? 0,
      showPricesWithVat: stored.showPricesWithVat ?? false,
    };
  }
  return {
    multiplesThreshold: data?.multiplesThreshold ?? 0,
    showPricesWithVat: data?.showPricesWithVat ?? false,
  };
}
